import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const loadTradingViewChart = (containerId, symbol) => {
  const container = document.getElementById(containerId);
  if (!container || !window.TradingView) return;
  container.innerHTML = '';
  const widgetDiv = document.createElement('div');
  widgetDiv.className = 'tradingview-widget-container__widget';
  widgetDiv.style.height = '500px';
  container.appendChild(widgetDiv);

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
  script.async = true;
  script.innerHTML = JSON.stringify({
    width: '100%',
    height: 500,
    symbol: `BINANCE:${symbol}`,
    interval: 'D',
    timezone: 'America/Sao_Paulo',
    theme: 'dark',
    style: '1',
    locale: 'br',
    enable_publishing: false,
    backgroundColor: 'rgba(11, 14, 17, 1)',
    gridColor: 'rgba(30, 34, 45, 0.5)',
    hide_top_toolbar: false,
    hide_legend: false,
    save_image: false,
    allow_symbol_change: true,
    studies: [],
    container_id: containerId,
  });
  container.appendChild(script);
};
import {
  fetchTickers,
  fetchBalances,
  submitSpotOrder,
} from '../api.js';

const ICON_CDN_BASE = 'https://cdn.jsdelivr.net/gh/vadimmalykhin/binance-icons';
const ICON_CDN_FALLBACK = 'https://cdn.jsdelivr.net/gh/madenix/Crypto-logo-cdn@main/Logos';

const handleIconError = (e, asset) => {
  const el = e.target;
  if (!el.dataset.fallback) {
    // First failure: try madenix CDN (uppercase ticker)
    el.dataset.fallback = '1';
    el.src = `${ICON_CDN_FALLBACK}/${asset.toUpperCase()}.svg`;
  } else if (el.dataset.fallback === '1') {
    // Second failure: default coin icon
    el.dataset.fallback = '2';
    el.src = '/default-coin.svg';
  }
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(Number(value));
};

const formatPercent = (value) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(2)}%`;
};

const changeClass = (value) => {
  if (value === null || value === undefined) return '';
  return value > 0 ? 'positive' : value < 0 ? 'negative' : '';
};

const StatusBadge = ({ status, delistTime }) => {
  if (!status) return <span>N/D</span>;
  let cls = 'status-badge';
  let label = status;
  if (status === 'TRADING') { cls += ' status-trading'; label = '✓ Trading'; }
  else if (status === 'HALT') { cls += ' status-halt'; label = '⚠ Halt'; }
  else if (status === 'BREAK') { cls += ' status-break'; label = '⏸ Break'; }
  else if (status === 'PRE_TRADING') { cls += ' status-pre'; label = '⏳ Pre-Trading'; }

  let delistBadge = null;
  if (delistTime) {
    const days = Math.ceil((new Date(delistTime) - new Date()) / 86400000);
    delistBadge = (
      <span className="status-badge status-delisting">
        🚨 {days > 0 ? `Delisting em ${days}d` : 'Delisted'}
      </span>
    );
  }

  return (
    <>
      <span className={cls}>{label}</span>
      {delistBadge && <><br />{delistBadge}</>}
    </>
  );
};

const TickerRow = ({ ticker, onNavigate, onBuy }) => {
  const [buyAmount, setBuyAmount] = useState('');
  const [chartExpanded, setChartExpanded] = useState(false);
  const chartContainerRef = useRef(null);
  const chartLoadedRef = useRef(false);
  const chg = ticker.change_percent !== null && !Number.isNaN(Number(ticker.change_percent))
    ? Number(ticker.change_percent) : null;

  const handleChartToggle = (e) => {
    e.stopPropagation();
    setChartExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (chartExpanded && !chartLoadedRef.current && chartContainerRef.current) {
      const containerId = `chart-${ticker.symbol}`;
      chartContainerRef.current.id = containerId;
      loadTradingViewChart(containerId, ticker.symbol);
      chartLoadedRef.current = true;
    }
  }, [chartExpanded, ticker.symbol]);

  return (
    <>
      <tr className="clickable-row">
        <td className="col-market" data-label="Par / Volume">
          <div className="market-cell">
            <button
              className={`chart-expand-btn${chartExpanded ? ' expanded' : ''}`}
              title="Expandir gráfico"
              onClick={handleChartToggle}
            >
              {chartExpanded ? '▼' : '▶'}
            </button>
            <img
              src={`${ICON_CDN_BASE}/crypto/${ticker.base_lower}.svg`}
              alt={ticker.base}
              className="pair-icon asset-icon"
              onError={(e) => handleIconError(e, ticker.base_lower)}
            />
            <div className="market-text">
              <span className="market-symbol">{ticker.symbol}</span>
              <span className="market-volume">Volume {ticker.quote_volume_display}</span>
            </div>
          </div>
        </td>
        <td className="col-price" data-label="Último Preço">{formatCurrency(ticker.price)}</td>
        <td className="col-change" data-label="Change(%)">
          {chg !== null ? (
            <span className={`change-value ${changeClass(chg)}`}>{formatPercent(chg)}</span>
          ) : 'N/D'}
        </td>
        <td data-label="Status"><StatusBadge status={ticker.status} delistTime={ticker.delist_time} /></td>
        <td data-label="Ações" className="actions-cell">
          <div className="ticker-actions">
            <input
              type="number"
              className="buy-input"
              placeholder="Qtd USDT"
              min="0"
              step="0.01"
              title="Valor em USDT para comprar"
              value={buyAmount}
              onChange={(e) => setBuyAmount(e.target.value)}
            />
            <button className="buy-btn-action" onClick={() => onBuy(ticker, buyAmount)} title="Comprar">
              Comprar
            </button>
            <button className="ghost-button" onClick={() => onNavigate(ticker.symbol)} title="Abrir trading">
              Trading
            </button>
          </div>
        </td>
      </tr>
      {chartExpanded && (
        <tr className="chart-collapse-row expanded">
          <td colSpan="5" className="chart-collapse-cell">
            <div className="chart-container">
              <div
                className="tradingview-widget-container"
                ref={chartContainerRef}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const BalanceRow = ({ balance, onSell, onSellAll }) => {
  const [sellAmount, setSellAmount] = useState('');
  const chg = balance.change_percent !== null && !Number.isNaN(Number(balance.change_percent))
    ? Number(balance.change_percent) : null;
  const profit = balance.profit_usdt !== null && !Number.isNaN(Number(balance.profit_usdt))
    ? Number(balance.profit_usdt) : null;
  const roi = balance.roi_percent !== null && !Number.isNaN(Number(balance.roi_percent))
    ? Number(balance.roi_percent) : null;
  const currentPrice = balance.total_usdt && balance.total > 0
    ? balance.total_usdt / balance.total : 0;

  return (
    <tr>
      <td data-label="Ícone">
        <img
          src={`${ICON_CDN_BASE}/crypto/${balance.asset.toLowerCase()}.svg`}
          alt={balance.asset}
          style={{ width: '32px', height: '32px' }}
          className="asset-icon"
          onError={(e) => handleIconError(e, balance.asset.toLowerCase())}
        />
      </td>
      <td data-label="Ativo">{balance.asset}</td>
      <td data-label="Disponível">{formatCurrency(balance.free)}</td>
      <td data-label="Bloqueado">{formatCurrency(balance.locked)}</td>
      <td data-label="Total">{formatCurrency(balance.total)}</td>
      <td data-label="Total em USDT">{balance.total_usdt !== null ? formatCurrency(balance.total_usdt) : 'N/D'}</td>
      <td data-label="Oscilação 24h %">
        {chg !== null ? <span className={`change-value ${changeClass(chg)}`}>{formatPercent(chg)}</span> : 'N/D'}
      </td>
      <td data-label="Profit (USDT)">
        {profit !== null ? <span className={`change-value ${changeClass(profit)}`}>{formatCurrency(profit)} USDT</span> : 'N/D'}
      </td>
      <td data-label="ROI %">
        {roi !== null ? <span className={`change-value ${changeClass(roi)}`}>{formatPercent(roi)}</span> : 'N/D'}
      </td>
      <td data-label="Status"><StatusBadge status={balance.status} delistTime={balance.delist_time} /></td>
      <td data-label="Ações" className="actions-cell">
        <div className="sell-actions">
          <input
            type="number"
            className="sell-input"
            placeholder="USDT"
            min="0"
            step="0.01"
            title="Valor em USDT para vender"
            value={sellAmount}
            onChange={(e) => setSellAmount(e.target.value)}
          />
          <button className="sell-btn-action" onClick={() => onSell(balance, sellAmount, setSellAmount)} title="Vender">
            Vender
          </button>
          <button className="sell-all-btn-action" onClick={() => onSellAll(balance)} title="Vender tudo">
            Vender Tudo
          </button>
        </div>
      </td>
    </tr>
  );
};

export default function HomeView() {
  const navigate = useNavigate();
  const [tickersData, setTickersData] = useState(null);
  const [balancesData, setBalancesData] = useState([]);
  const [balancesResponse, setBalancesResponse] = useState(null);
  const [currentQuote, setCurrentQuote] = useState('USDT');
  const [hideSmall, setHideSmall] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const loadData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const [tickers, balances] = await Promise.all([fetchTickers(), fetchBalances()]);
      setTickersData(tickers);
      setBalancesData(Array.isArray(balances.balances) ? balances.balances : []);
      setBalancesResponse(balances);
      if (tickers.quote_tabs?.length > 0) {
        setCurrentQuote(tickers.quote_tabs[0]);
      }
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    loadData();
    intervalRef.current = setInterval(loadData, 30000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleBuy = async (ticker, amount) => {
    const usdtAmount = parseFloat(amount);
    if (!usdtAmount || usdtAmount <= 0) {
      alert('Por favor, insira um valor válido em USDT para comprar.');
      return;
    }
    if (!confirm(`Comprar ${ticker.base} com ${usdtAmount.toFixed(2)} USDT?\n\nTipo: Market Order`)) return;
    try {
      const data = await submitSpotOrder({
        symbol: ticker.symbol,
        side: 'BUY',
        type: 'MARKET',
        quoteOrderQty: usdtAmount,
      });
      alert(`Ordem executada!\nSímbolo: ${ticker.symbol}\nStatus: ${data.status || 'FILLED'}`);
      await loadData();
    } catch (err) {
      alert(`Erro ao executar ordem: ${err.message}`);
    }
  };

  const handleSell = async (balance, amount, setSellAmount) => {
    const usdtValue = parseFloat(amount);
    if (!usdtValue || usdtValue <= 0) {
      alert('Por favor, insira um valor válido em USDT para vender.');
      return;
    }
    const currentPrice = balance.total_usdt && balance.total > 0 ? balance.total_usdt / balance.total : 0;
    if (!currentPrice || currentPrice <= 0) {
      alert('Preço atual indisponível.');
      return;
    }
    const sellQty = usdtValue / currentPrice;
    if (sellQty > balance.free) {
      alert(`Valor insuficiente. Disponível: ${(balance.free * currentPrice).toFixed(2)} USDT`);
      return;
    }
    if (!confirm(`Vender ${sellQty.toFixed(8)} ${balance.asset} (~${usdtValue.toFixed(2)} USDT)?`)) return;
    try {
      const data = await submitSpotOrder({
        symbol: `${balance.asset}USDT`,
        side: 'SELL',
        type: 'MARKET',
        quantity: sellQty,
      });
      alert(`Ordem executada!\nStatus: ${data.status || 'FILLED'}`);
      setSellAmount('');
      await loadData();
    } catch (err) {
      alert(`Erro ao executar ordem: ${err.message}`);
    }
  };

  const handleSellAll = async (balance) => {
    if (!balance.free || balance.free <= 0) {
      alert('Nenhum saldo disponível para vender.');
      return;
    }
    if (!confirm(`Vender TUDO (${balance.free.toFixed(8)} ${balance.asset})?`)) return;
    try {
      const data = await submitSpotOrder({
        symbol: `${balance.asset}USDT`,
        side: 'SELL',
        type: 'MARKET',
        quantity: balance.free,
      });
      alert(`Ordem executada!\nStatus: ${data.status || 'FILLED'}`);
      await loadData();
    } catch (err) {
      alert(`Erro ao executar ordem: ${err.message}`);
    }
  };

  const btcTicker = tickersData?.tickers_by_quote?.USDT?.find((t) => t.symbol === 'BTCUSDT');

  useEffect(() => {
    if (btcTicker?.price) {
      const price = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(btcTicker.price));
      const chg = btcTicker.change_percent !== null ? Number(btcTicker.change_percent) : null;
      if (chg !== null) {
        const emoji = chg >= 0 ? '�' : '�';
        document.title = `${emoji} BTC $${price} (${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%)`;
      } else {
        document.title = `BTC $${price}`;
      }
    } else {
      document.title = 'Mid Trader';
    }
  }, [btcTicker?.price, btcTicker?.change_percent]);

  const filteredBalances = balancesData
    .filter((b) => !hideSmall || (b.total_usdt ?? 0) >= 1)
    .sort((a, b) => (b.total_usdt ?? -Infinity) - (a.total_usdt ?? -Infinity));

  const totalAllocation = balancesData
    .filter((b) => b.asset !== 'USDT')
    .reduce((sum, b) => sum + (b.total_usdt ?? 0), 0);
  const numAssets = balancesData.filter((b) => b.asset !== 'USDT' && (b.total_usdt ?? 0) > 0).length;

  const currentTickers = tickersData?.tickers_by_quote?.[currentQuote] || [];

  return (
    <div className="view view-home">
      <header className="view-header">
        <div>
          <h1>Dashboard Binance</h1>
          <p className="view-subtitle">Cotações spot e saldos da carteira em tempo real.</p>
        </div>
        <button className="button-link" onClick={loadData} disabled={loading}>
          <span aria-hidden="true">⟳</span>
          Atualizar
        </button>
      </header>

      <div className="home-grid">
        <div className="balance-cards-row">
          <section className="card" aria-labelledby="balance-title">
            <header className="card-header">
              <h2 id="balance-title">Balanço Total</h2>
            </header>
            <div className="balance-summary">
              {error ? (
                <p className="highlight">Erro: {error}</p>
              ) : balancesResponse ? (
                <>
                  <p className="highlight">
                    {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(balancesResponse.total_balance || 0)} USDT{' '}
                    {typeof balancesResponse.balance_change_yesterday_percent === 'number' && (
                      <span className={`change-value ${changeClass(balancesResponse.balance_change_yesterday_percent)}`}>
                        {balancesResponse.balance_change_yesterday_percent >= 0 ? '+' : ''}
                        {balancesResponse.balance_change_yesterday_percent.toFixed(2)}%
                      </span>
                    )}
                  </p>
                  <p className="balance-subtext">
                    Spot: {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(balancesResponse.spot_balance || 0)} USDT
                    {typeof balancesResponse.usdt_in_coins === 'number' && ` (${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(balancesResponse.usdt_in_coins)} USDT em ${balancesResponse.num_coins || 0} moedas)`}
                    {' | '}
                    Earn: {typeof balancesResponse.earn_balance === 'number'
                      ? `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(balancesResponse.earn_balance)} USDT`
                      : '—'}
                  </p>
                </>
              ) : (
                <p className="highlight">Carregando...</p>
              )}
            </div>
          </section>

          <section className="card" aria-labelledby="bitcoin-title">
            <header className="card-header">
              <h2 id="bitcoin-title">Bitcoin (BTC)</h2>
            </header>
            <div className="balance-summary">
              {btcTicker ? (
                <>
                  <p className="highlight">
                    ${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(btcTicker.price))}
                  </p>
                  <p className="balance-subtext">
                    Oscilação 24h:{' '}
                    {btcTicker.change_percent !== null ? (
                      <span className={`change-value ${changeClass(Number(btcTicker.change_percent))}`}>
                        {Number(btcTicker.change_percent) >= 0 ? '+' : ''}
                        {Number(btcTicker.change_percent).toFixed(2)}%
                      </span>
                    ) : 'N/D'}
                  </p>
                </>
              ) : (
                <>
                  <p className="highlight">Carregando...</p>
                  <p className="balance-subtext">Carregando...</p>
                </>
              )}
            </div>
          </section>
        </div>

        <section className="card" aria-labelledby="balances-title">
          <header className="card-header">
            <h2 id="balances-title">Distribuição por Ativo</h2>
          </header>
          <div className="balances-controls">
            <label>
              <input type="checkbox" checked={hideSmall} onChange={(e) => setHideSmall(e.target.checked)} />
              Ocultar ativos &lt; 1 USDT
            </label>
            <p className="balance-total-allocation">
              Total alocado: {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(totalAllocation)} USDT em {numAssets} {numAssets === 1 ? 'ativo' : 'ativos'}
            </p>
          </div>
          <div className="table-container">
            <table className="home-table">
              <thead>
                <tr>
                  <th>Ícone</th><th>Ativo</th><th>Disponível</th><th>Bloqueado</th>
                  <th>Total</th><th>Total em USDT</th><th>Oscilação 24h %</th>
                  <th>Profit (USDT)</th><th>ROI %</th><th>Status</th><th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr><td colSpan="11" className="empty">Erro ao carregar saldos: {error}</td></tr>
                ) : filteredBalances.length === 0 ? (
                  <tr><td colSpan="11" className="empty">Carregando saldos...</td></tr>
                ) : (
                  filteredBalances.map((balance) => (
                    <BalanceRow
                      key={balance.asset}
                      balance={balance}
                      onSell={handleSell}
                      onSellAll={handleSellAll}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card pairs-card" aria-labelledby="tickers-title">
          <header className="card-header">
            <h2 id="tickers-title">Cotações Spot</h2>
            <div className="pairs-tabs" role="tablist">
              {tickersData?.quote_tabs?.map((quote, i) => (
                <button
                  key={quote}
                  className={`pairs-tab${quote === currentQuote ? ' active' : ''}`}
                  role="tab"
                  aria-selected={quote === currentQuote}
                  onClick={() => setCurrentQuote(quote)}
                >
                  {quote}
                </button>
              ))}
            </div>
          </header>
          <div className="pairs-content">
            <div className="pairs-panel active" role="tabpanel">
              <table className="pairs-table">
                <thead>
                  <tr>
                    <th className="col-market">Par / Volume</th>
                    <th className="col-price">Último Preço</th>
                    <th className="col-change">Change(%)</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {error ? (
                    <tr><td colSpan="6" className="empty">Erro ao carregar cotações: {error}</td></tr>
                  ) : currentTickers.length === 0 ? (
                    <tr><td colSpan="6" className="empty">Carregando cotações...</td></tr>
                  ) : (
                    currentTickers
                      .slice()
                      .sort((a, b) => {
                        const av = a.change_percent !== null ? Number(a.change_percent) : Infinity;
                        const bv = b.change_percent !== null ? Number(b.change_percent) : Infinity;
                        return av - bv;
                      })
                      .map((ticker) => (
                        <TickerRow
                          key={ticker.symbol}
                          ticker={ticker}
                          onNavigate={(sym) => navigate(`/pair/${sym}`)}
                          onBuy={handleBuy}
                        />
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
